import {
  createContext,
  FC,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { submitPostForm } from './postForm';

import { captureLoginMethod } from '@/lib/idp/analytics';
import {
  IDP_CALLBACK_PATH,
  IdpConfig,
  idpEnvDoBuild,
  idpPostLogoutRedirectUri,
  idpRedirectUri,
  idpSemContaLogoutRedirectUri,
  readIdpConfig,
  SEM_CONTA_PATH,
} from '@/lib/idp/config';
import { getIdpProvider } from '@/lib/idp/get-provider';
import { logIdp } from '@/lib/idp/log';
import {
  clearIdpSession,
  clearPendingLogin,
  consumeIdpSession,
  markIdpSession,
  PendingLogin,
  savePendingLogin,
  takePendingLogin,
} from '@/lib/idp/login-flow';
import { chegouComMarcaDeSso, rotaSemMarcaDeSso } from '@/lib/idp/marca-sso';
import { challengeFrom, createCodeVerifier, createNonce, createState } from '@/lib/idp/pkce';
import { IdpAuthError, IdpProvider, IdpSession, IdpTokens } from '@/lib/idp/provider';
import { registrarRecusaDaApi } from '@/lib/idp/recusa-da-api';
import {
  clearIdpTokens,
  getIdpTokens,
  setIdpTokens,
  subscribeIdpTokens,
} from '@/lib/idp/token-store';
import { temVinculo } from '@/lib/idp/vinculo';
import { dentroDeMoldura } from '@/lib/moldura';

/**
 * A sessao do IDP dentro do app.
 *
 * Mora acima do `UserProvider` na arvore porque e ele quem decide, no boot, se
 * a sessao ativa e a do IDP (memoria) ou a legada (localStorage) — e porque o
 * callback do OIDC precisa fechar antes de qualquer tela montar, inclusive a
 * propria rota `/auth/callback`.
 */

export type IdpStatus = 'loading' | 'anonymous' | 'authenticated' | 'no-vinculo';

export interface IdpAuthContextType {
  readonly status: IdpStatus;
  readonly session?: IdpSession;
  readonly config: IdpConfig;
  /** Para onde a rota de callback deve mandar o usuario depois de fechar o fluxo. */
  readonly returnTo?: string;
  /** Mensagem pronta para a tela quando o login falhou. */
  readonly error?: string;
  /**
   * Comeca o fluxo: leva o navegador para a tela do IDP. `returnTo` e o
   * deep-link para onde voltar; sem ele, a rota atual.
   */
  login(returnTo?: string): Promise<void>;
  /** Logout RP-initiated: derruba a sessao local e a SSO do IDP. */
  logout(): Promise<void>;
  /** Derruba so a sessao local do IDP, sem tocar na SSO. */
  clearLocal(): void;
  /**
   * O "Sair" da `/sem-conta`: refaz o logout federado quando o do callback
   * falhou. Devolve `true` quando saiu do app para o IDP; `false` quando nao
   * havia logout pendente (ou ele falhou de novo) e quem chama segue sozinho.
   */
  sairSemConta(): Promise<boolean>;
  /**
   * Renova a sessao do IDP agora e devolve o access token novo, para os links
   * que levam token na URL (Retool e GeoSeazone — excecao do ADR 005).
   * `undefined` sem sessao do IDP, sem refresh token ou quando a renovacao
   * falha; a falha nao derruba a sessao, so o link sai sem token.
   */
  renovarTokenParaLink(): Promise<string | undefined>;
}

const configVazia: IdpConfig = {
  loginFlag: 'off',
  enabled: false,
  passwordLogin: true,
  authProvider: 'keycloak',
  clientId: 'sapron-frontend',
  mockSemVinculo: false,
};

export const IdpAuthContext = createContext<IdpAuthContextType>({
  status: 'anonymous',
  config: configVazia,
  login: async () => {},
  logout: async () => {},
  clearLocal: () => {},
  sairSemConta: async () => false,
  renovarTokenParaLink: async () => undefined,
});

/** Renovar um pouco antes do fim evita a corrida com o `exp` de 300s do realm. */
const ANTECEDENCIA_DO_REFRESH_S = 60;
const ESPERA_MINIMA_MS = 5000;
const MS_POR_SEGUNDO = 1000;

function mensagemDe(error: unknown): string {
  return error instanceof IdpAuthError
    ? 'Não foi possível concluir o login com a Seazone. Tente de novo.'
    : 'Não foi possível falar com o login da Seazone. Tente de novo em instantes.';
}

function codigoDe(error: unknown): string {
  return error instanceof IdpAuthError ? error.code : 'erro_inesperado';
}

/**
 * Leva o app para a `/sem-conta` sem recarregar a pagina. O provider mora
 * acima do `BrowserRouter` e nao tem `navigate`; o react-router escuta o
 * `popstate` e le a rota de `window.location`. Recarregar perderia o logout
 * pendente, que so vive em memoria.
 */
function irParaSemConta(): void {
  window.history.replaceState(null, '', SEM_CONTA_PATH);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** Path + query + hash de onde o usuario estava quando o fluxo comecou. */
function rotaAtual(): string {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
}

export const IdpAuthProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const config = useMemo(() => readIdpConfig(idpEnvDoBuild()), []);

  const [status, setStatus] = useState<IdpStatus>('loading');
  const [session, setSession] = useState<IdpSession | undefined>(undefined);
  const [returnTo, setReturnTo] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [tokens, setTokens] = useState<IdpTokens | undefined>(getIdpTokens);

  const providerRef = useRef<IdpProvider | undefined>(undefined);
  const bootRef = useRef(false);
  /**
   * Sobe a cada limpeza da sessao. Um refresh que saiu antes do "Sair" e volta
   * depois confere a geracao e descarta a resposta, senao repunha o token.
   */
  const geracaoRef = useRef(0);
  /**
   * O `id_token` de quem entrou sem vinculo e cujo logout federado falhou no
   * callback. Fica fora do token store de proposito: sem vinculo nao existe
   * sessao, e o interceptor nao pode mandar esse token para a API. Vive so ate
   * o "Sair" da `/sem-conta` conseguir refazer o logout.
   */
  const logoutPendenteRef = useRef<string | undefined>(undefined);

  const provider = useCallback((): IdpProvider => {
    if (providerRef.current === undefined) providerRef.current = getIdpProvider(config);
    return providerRef.current;
  }, [config]);

  useEffect(() => subscribeIdpTokens(setTokens), []);

  /**
   * Logout RP-initiated: POST no `end_session_endpoint` com o `id_token_hint`.
   * `saiu` = o navegador foi para o IDP; `sem-sessao-central` = provider sem
   * SSO para encerrar (o duble); `falhou` = o IDP nao respondeu (discovery
   * fora do ar). Nos dois ultimos o app continua aqui e quem chama decide.
   */
  const encerrarSso = useCallback(
    async (
      idToken: string | undefined,
      postLogoutRedirectUri: string,
    ): Promise<'saiu' | 'sem-sessao-central' | 'falhou'> => {
      let saida: { url: string; params: Record<string, string> } | undefined;
      try {
        saida = await provider().endSession?.({
          ...(idToken !== undefined && { idToken }),
          postLogoutRedirectUri,
        });
      } catch {
        return 'falhou';
      }

      if (saida === undefined) return 'sem-sessao-central';
      submitPostForm(saida.url, saida.params);
      return 'saiu';
    },
    [provider],
  );

  const falhar = useCallback((event: 'login' | 'refresh', causa: unknown, silent = false) => {
    logIdp(event === 'login' ? 'idp_login.failed' : 'idp_refresh.failed', {
      reason: codigoDe(causa),
      silent,
    });
    clearIdpTokens();
    clearIdpSession();
    setSession(undefined);
    setStatus('anonymous');
    // Falha de tentativa silenciosa nao vira tela de erro: o usuario nao pediu
    // nada, so abriu o app de novo. Ele cai na tela de login normal.
    setError(silent ? undefined : mensagemDe(causa));
  }, []);

  const iniciar = useCallback(
    async (silent: boolean, destino: string) => {
      // Dentro da moldura o Keycloak e o `/auth/callback` recusam ser
      // carregados (`X-Frame-Options`): nenhum login pelo IDP sai dali, nem o
      // silencioso nem o pedido. A moldura entra pelo handshake ou pela senha.
      if (dentroDeMoldura()) {
        throw new IdpAuthError('em_moldura', 'Login pelo IDP indisponivel dentro de iframe.');
      }

      const state = createState();
      const nonce = createNonce();
      const codeVerifier = createCodeVerifier();
      const pending: PendingLogin = { state, nonce, codeVerifier, returnTo: destino, silent };

      savePendingLogin(pending);

      const url = await provider().authorizeUrl({
        state,
        nonce,
        codeChallenge: await challengeFrom(codeVerifier),
        redirectUri: idpRedirectUri(window.location.origin),
        ...(silent && { silent }),
      });

      window.location.assign(url);
    },
    [provider],
  );

  const concluir = useCallback(
    async (pending: PendingLogin, code: string) => {
      const emitidos = await provider().exchangeCode({
        code,
        codeVerifier: pending.codeVerifier,
        nonce: pending.nonce,
        redirectUri: idpRedirectUri(window.location.origin),
      });
      const nova = await provider().toSession(emitidos, { nonce: pending.nonce });

      setReturnTo(pending.returnTo);

      if (!temVinculo(nova)) {
        // Autenticado nao e autorizado: conta criada no IDP que o Sapron nao
        // conhece nao ganha sessao nenhuma aqui, nem em memoria.
        logIdp('idp_login.no_vinculo', { sub: nova.sub, silent: pending.silent });
        clearIdpTokens();
        clearIdpSession();
        setSession(undefined);
        setStatus('no-vinculo');
        // Logout federado no mesmo passo (design, "pagina clara + logout
        // federado"): sem ele a SSO do Keycloak continua viva e o proximo
        // "Entrar com Seazone" volta direto a esta parede, sem pedir
        // credencial. O Keycloak devolve o navegador para a `/sem-conta`.
        const resultado = await encerrarSso(
          emitidos.idToken,
          idpSemContaLogoutRedirectUri(window.location.origin),
        );
        if (resultado === 'saiu') logIdp('idp_logout.ok', { reason: 'no_vinculo' });
        if (resultado === 'falhou') {
          // A SSO continua viva: o "Sair" da `/sem-conta` tenta de novo.
          logoutPendenteRef.current = emitidos.idToken;
          logIdp('idp_login.failed', { reason: 'end_session_no_vinculo' });
        }
        return;
      }

      setIdpTokens(emitidos);
      markIdpSession();
      setSession(nova);
      setStatus('authenticated');
      logIdp('idp_login.ok', {
        sub: nova.sub,
        silent: pending.silent,
        vinculo: nova.sapronUserId !== undefined,
      });
      // So o login que o usuario pediu conta como conversao; a restauracao
      // silenciosa depois de um F5 inflaria o evento.
      if (!pending.silent) captureLoginMethod('idp');
    },
    [encerrarSso, provider],
  );

  const tratarCallback = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const erroDoIdp = params.get('error');
    const code = params.get('code');

    let pending: PendingLogin | undefined;
    try {
      pending = takePendingLogin(params.get('state'));
    } catch (causa) {
      falhar('login', causa);
      return;
    }

    if (erroDoIdp !== null || code === null) {
      // `login_required` e a resposta normal de `prompt=none` sem sessao SSO.
      setReturnTo(pending.returnTo);
      falhar(
        'login',
        new IdpAuthError(erroDoIdp ?? 'callback_sem_code', 'Callback sem code.'),
        pending.silent,
      );
      return;
    }

    try {
      await concluir(pending, code);
    } catch (causa) {
      falhar('login', causa, pending.silent);
    }
  }, [concluir, falhar]);

  useEffect(() => {
    if (bootRef.current) return;
    bootRef.current = true;

    const noCallback = window.location.pathname.replace(/\/+$/, '') === IDP_CALLBACK_PATH;
    const tinhaSessao = consumeIdpSession();
    // Veio de outra app do realm (a Wallet) com sessao do IDP: a aba nao tem
    // marca propria, mas a sessao SSO do Keycloak esta viva.
    const marcaDeSso = chegouComMarcaDeSso(window.location);

    if (noCallback) {
      tratarCallback();
      return;
    }

    clearPendingLogin();

    // Dentro de iframe (o Programa de Indicacao embutido no portal do
    // franqueado) a tentativa silenciosa navega a moldura para o IDP e volta
    // por `/auth/callback`, que fica fora de `/parceiros/*` e responde
    // `X-Frame-Options: SAMEORIGIN`: o navegador recusa e a moldura quebra.
    // E o iframe abre com `?target=partner` sem token, que conta como marca de
    // SSO. Ali a sessao chega pelo handshake de `postMessage`, nao pelo IDP.
    if (dentroDeMoldura()) {
      setStatus('anonymous');
      return;
    }

    if ((!tinhaSessao && !marcaDeSso) || !config.enabled) {
      if (config.problem !== undefined) {
        logIdp('idp_login.failed', { reason: 'config', detail: config.problem });
      }
      setStatus('anonymous');
      return;
    }

    // Recarregar a pagina apaga os tokens (eles so vivem em memoria). Quem
    // devolve a sessao e a SSO do IDP, por `prompt=none`: volta sem tela quando
    // a sessao central esta viva, e responde `login_required` quando nao esta.
    // A mesma tentativa restaura a sessao de quem chega pela marca de SSO; o
    // destino volta sem a marca, senao um reload tentaria de novo.
    const destino = marcaDeSso ? rotaSemMarcaDeSso(window.location) : rotaAtual();
    iniciar(true, destino).catch((causa: unknown) => falhar('login', causa, true));
  }, [config, falhar, iniciar, tratarCallback]);

  useEffect(() => {
    if (status !== 'authenticated' || tokens?.refreshToken === undefined) return undefined;

    const emMs = tokens.expiresAt * MS_POR_SEGUNDO - ANTECEDENCIA_DO_REFRESH_S * MS_POR_SEGUNDO;
    const espera = Math.max(emMs - Date.now(), ESPERA_MINIMA_MS);
    const { refreshToken } = tokens;

    const timer = setTimeout(() => {
      const geracao = geracaoRef.current;
      const vigente = () => geracaoRef.current === geracao;
      provider()
        .refresh(refreshToken)
        .then(async (renovados) => {
          if (!vigente()) return;
          setIdpTokens(renovados);
          const renovada = await provider().toSession(renovados);
          if (vigente()) setSession(renovada);
        })
        .catch((causa: unknown) => {
          if (vigente()) falhar('refresh', causa);
        });
    }, espera);

    return () => clearTimeout(timer);
  }, [falhar, provider, status, tokens]);

  /**
   * A API recusou (401) uma requisicao feita com o token do IDP: a conta passou
   * no portao do callback, mas o Sapron nao a reconhece. Mesmo desfecho do
   * sem-vinculo no callback: nenhuma sessao, logout federado e `/sem-conta`.
   * Chamado pelo interceptor do axios via `registrarRecusaDaApi`.
   */
  const recusadoPelaApi = useCallback(async () => {
    const tokensAtuais = getIdpTokens();
    // Varias requisicoes podem voltar 401 juntas; so a primeira encontra
    // sessao para derrubar.
    if (tokensAtuais === undefined) return;

    geracaoRef.current += 1;
    logIdp('idp_login.no_vinculo', { reason: 'sapron_api_recusou' });
    clearIdpTokens();
    clearIdpSession();
    clearPendingLogin();
    setSession(undefined);
    setStatus('no-vinculo');
    irParaSemConta();

    const resultado = await encerrarSso(
      tokensAtuais.idToken,
      idpSemContaLogoutRedirectUri(window.location.origin),
    );
    if (resultado === 'saiu') logIdp('idp_logout.ok', { reason: 'no_vinculo' });
    if (resultado === 'falhou') {
      // A SSO continua viva: o "Sair" da `/sem-conta` tenta de novo.
      logoutPendenteRef.current = tokensAtuais.idToken;
      logIdp('idp_login.failed', { reason: 'end_session_no_vinculo' });
    }
  }, [encerrarSso]);

  useEffect(
    () =>
      registrarRecusaDaApi(() => {
        recusadoPelaApi();
      }),
    [recusadoPelaApi],
  );

  const clearLocal = useCallback(() => {
    geracaoRef.current += 1;
    logoutPendenteRef.current = undefined;
    clearIdpTokens();
    clearIdpSession();
    clearPendingLogin();
    setSession(undefined);
    setStatus('anonymous');
  }, []);

  const login = useCallback(
    async (destino?: string) => {
      setError(undefined);
      try {
        await iniciar(false, destino ?? rotaAtual());
      } catch (causa) {
        falhar('login', causa);
      }
    },
    [falhar, iniciar],
  );

  const logout = useCallback(async () => {
    const idToken = getIdpTokens()?.idToken;
    clearLocal();
    logIdp('idp_logout.ok');

    const resultado = await encerrarSso(idToken, idpPostLogoutRedirectUri(window.location.origin));
    // Provider sem sessao central (o duble): o logout do app e so o local.
    if (resultado !== 'saiu') window.location.assign('/');
  }, [clearLocal, encerrarSso]);

  const sairSemConta = useCallback(async (): Promise<boolean> => {
    const idToken = logoutPendenteRef.current;
    if (idToken === undefined) return false;

    const resultado = await encerrarSso(idToken, `${window.location.origin}/login`);
    if (resultado !== 'saiu') return false;

    logoutPendenteRef.current = undefined;
    logIdp('idp_logout.ok', { reason: 'no_vinculo_retry' });
    return true;
  }, [encerrarSso]);

  const renovarTokenParaLink = useCallback(async (): Promise<string | undefined> => {
    const atuais = getIdpTokens();
    if (atuais === undefined) return undefined;
    if (atuais.refreshToken === undefined) {
      logIdp('idp_refresh.failed', { reason: 'sem_refresh_token', origem: 'link' });
      return undefined;
    }

    const geracao = geracaoRef.current;
    const vigente = () => geracaoRef.current === geracao;
    try {
      const renovados = await provider().refresh(atuais.refreshToken);
      const renovada = await provider().toSession(renovados);
      // Saiu (ou a API recusou a sessao) enquanto renovava: nao repoe o token.
      if (!vigente()) return undefined;
      setIdpTokens(renovados);
      setSession(renovada);
      return renovados.accessToken;
    } catch (causa) {
      // Diferente do timer: quem decide a sessao e ele. Aqui o link so sai sem token.
      logIdp('idp_refresh.failed', { reason: codigoDe(causa), origem: 'link' });
      return undefined;
    }
  }, [provider]);

  const value = useMemo<IdpAuthContextType>(
    () => ({
      status,
      ...(session !== undefined && { session }),
      config,
      ...(returnTo !== undefined && { returnTo }),
      ...(error !== undefined && { error }),
      login,
      logout,
      clearLocal,
      sairSemConta,
      renovarTokenParaLink,
    }),
    [
      clearLocal,
      config,
      error,
      login,
      logout,
      renovarTokenParaLink,
      returnTo,
      sairSemConta,
      session,
      status,
    ],
  );

  return <IdpAuthContext.Provider value={value}>{children}</IdpAuthContext.Provider>;
};

export const useIdpAuth = () => useContext(IdpAuthContext);
